import type { Dispatch, SetStateAction } from "react";
import type { AllocationInput, DashboardData, ExpenseInput } from "../../types";
import { minorAmount } from "../../lib/allocations";
import { formatMinor } from "../../lib/money-math";
import { money } from "../../lib/finance";
import EntryCategoryFields from "./EntryCategoryFields";

export default function CategorySplitFields({ input, setInput, entryOptions, onOptionsSaved, setCategoryError, currency, disabled }: {
  input: ExpenseInput;
  setInput: Dispatch<SetStateAction<ExpenseInput>>;
  entryOptions: DashboardData["entryOptions"];
  onOptionsSaved: (options: DashboardData["entryOptions"]) => void;
  setCategoryError: (message: string) => void;
  currency: string;
  disabled?: boolean;
}) {
  const parts = input.allocations;
  const share = input.reportableAmount !== undefined;
  function changeParts(change: AllocationInput[] | ((current: AllocationInput[]) => AllocationInput[])) {
    setInput(v => {
      const allocations = typeof change === "function" ? change(v.allocations || []) : change;
      return {
        ...v, allocations,
        category: allocations[0]?.category || "Uncategorized",
        subcategory: allocations[0]?.subcategory || "",
        ...(v.reportableAmount !== undefined ? {
          reportableAmount: formatMinor(allocations.reduce((sum, p) => sum + (minorAmount(p.reportableAmount ?? p.amount) ?? 0), 0)),
        } : {}),
      };
    });
  }
  if (!parts) return <button type="button" className="text-button" disabled={disabled} onClick={() => changeParts([
    { category: input.category, subcategory: input.subcategory, amount: input.amount, ...(share ? { reportableAmount: input.reportableAmount } : {}) },
    { category: "", subcategory: "", amount: "", ...(share ? { reportableAmount: "" } : {}) },
  ])}>Split into categories</button>;
  const allocated = parts.reduce((sum, p) => sum + (minorAmount(p.amount) ?? 0), 0);
  const total = minorAmount(input.amount) ?? 0;
  const remaining = total - allocated;
  const update = (index: number, patch: Partial<AllocationInput>) => changeParts(current => current.map((p, i) => i === index ? { ...p, ...patch } : p));
  return <section className="category-splits" aria-label="Category split">
    <div className="split-heading"><strong>Category split</strong><button type="button" className="text-button" onClick={() => setInput(v => ({ ...v, allocations: undefined }))}>Use one category</button></div>
    {parts.map((part, index) => <fieldset className="split-row" key={index}>
      <legend>Category {index + 1}</legend>
      <div className="split-category-fields">
        <EntryCategoryFields input={{ ...input, ...part }} setInput={action => {
          const current = { ...input, ...part };
          const next = typeof action === "function" ? action(current) : action;
          update(index, { category: next.category, subcategory: next.subcategory });
        }} field={(key, value) => update(index, key === "category" ? { category: value, subcategory: "" } : { subcategory: value })} isIncome={input.type === "Income"} entryOptions={entryOptions} onOptionsSaved={onOptionsSaved} setCategoryError={setCategoryError} />
      </div>
      <div className="split-amount-row">
        <label>Amount ({currency})<input aria-label={`Category ${index + 1} amount`} inputMode="decimal" pattern="[0-9]+([.][0-9]{1,2})?" required maxLength={12} value={part.amount} onChange={e => update(index, { amount: e.target.value })} placeholder="0.00" /></label>
        <button type="button" className="text-button" disabled={remaining + (minorAmount(part.amount) ?? 0) <= 0} onClick={() => update(index, { amount: formatMinor(remaining + (minorAmount(part.amount) ?? 0)) })}>Use remainder</button>
        <button type="button" className="text-button" aria-label={`Remove category ${index + 1}`} disabled={parts.length === 1} onClick={() => changeParts(parts.filter((_, i) => i !== index))}>Remove</button>
      </div>
      {share && <label>Household share ({currency})<input aria-label={`Category ${index + 1} household share`} inputMode="decimal" pattern="[0-9]+([.][0-9]{1,2})?" required value={part.reportableAmount ?? part.amount} onChange={e => update(index, { reportableAmount: e.target.value })} /></label>}
    </fieldset>)}
    <div className="split-footer">
      <button type="button" className="text-button" disabled={parts.length >= 50} onClick={() => changeParts([...parts, { category: "", subcategory: "", amount: "", ...(share ? { reportableAmount: "" } : {}) }])}>+ Add category</button>
      <p role="status">{remaining === 0 ? `Allocated: ${money(allocated / 100, currency)} of ${money(total / 100, currency)} ✓` : remaining > 0 ? `${money(remaining / 100, currency)} left to allocate` : `${money(-remaining / 100, currency)} over the total`}</p>
    </div>
    <label className="household-share-toggle"><input type="checkbox" checked={share} onChange={e => setInput(v => ({ ...v, reportableAmount: e.target.checked ? formatMinor(allocated) : undefined, allocations: v.allocations?.map(p => ({ ...p, reportableAmount: e.target.checked ? p.amount : undefined })) }))} />Set household share by category</label>
    {share && <small>Only each category’s household share counts in reports. The payment stays {money(total / 100, currency)}.</small>}
  </section>;
}
